<script setup lang="ts">
import { isValidFriendSteamID } from '@/lib/playerSocialPresentation'
import { computed, nextTick, onMounted, ref } from 'vue'

const props = defineProps<{
  currentSteamId: string
  draft: string
  actionPending: boolean
  error: string | null
}>()

const emit = defineEmits<{
  close: []
  submit: []
  clearError: []
  'update:draft': [value: string]
}>()

const input = ref<HTMLInputElement | null>(null)

const normalizedDraft = computed(() => props.draft.trim())
const validDraft = computed(() => (
  isValidFriendSteamID(normalizedDraft.value, props.currentSteamId)
))
const validationMessage = computed(() => {
  if (!normalizedDraft.value) return ''
  if (normalizedDraft.value === props.currentSteamId) {
    return 'Нельзя добавить самого себя.'
  }
  if (!validDraft.value) return 'Введите корректный SteamID64.'
  return ''
})

function updateDraft(event: Event): void {
  emit('update:draft', (event.target as HTMLInputElement).value)
}

onMounted(() => {
  void nextTick(() => input.value?.focus())
})
</script>

<template>
  <div class="player-add-friend-layer" @pointerdown.stop>
    <button
      class="player-add-friend-backdrop"
      type="button"
      aria-label="Закрыть окно добавления друга"
      tabindex="-1"
      :disabled="actionPending"
      @click="$emit('close')"
    ></button>

    <section
      id="player-add-friend-dialog"
      class="player-add-friend-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="player-add-friend-title"
      :aria-busy="actionPending"
      tabindex="-1"
    >
      <header>
        <h2 id="player-add-friend-title">Добавить друга</h2>
        <p>Введите SteamID64 игрока, которому хотите отправить заявку.</p>
      </header>

      <form @submit.prevent="$emit('submit')">
        <label for="friend-steam-id">SteamID64 друга</label>
        <input
          id="friend-steam-id"
          ref="input"
          :value="draft"
          inputmode="numeric"
          autocomplete="off"
          placeholder="7656119…"
          pattern="[1-9][0-9]*"
          :disabled="actionPending"
          @input="updateDraft"
        />
        <p v-if="validationMessage" class="player-add-friend-validation" role="status">
          {{ validationMessage }}
        </p>

        <p class="player-add-friend-self-id">
          Ваш SteamID64: <strong>{{ currentSteamId }}</strong>
        </p>

        <div v-if="error" class="player-dock-alert" role="alert">
          <span>{{ error }}</span>
          <button type="button" aria-label="Скрыть ошибку" @click="$emit('clearError')">×</button>
        </div>

        <footer>
          <button
            class="player-add-friend-submit"
            type="submit"
            :disabled="actionPending || !validDraft"
          >
            {{ actionPending ? 'Отправляем…' : 'Отправить заявку' }}
          </button>
          <button
            class="player-add-friend-cancel"
            type="button"
            :disabled="actionPending"
            @click="$emit('close')"
          >
            Отмена
          </button>
        </footer>
      </form>
    </section>
  </div>
</template>
